/**
 * Утилита для перехвата console.log/warn/error и сохранения в IndexedDB
 * Логи сохраняются в том же формате как они выводятся в консоль
 * Также хранит последние логи в памяти для мгновенного доступа
 * 
 * ОПТИМИЗАЦИИ для масштабирования (100+ сотрудников):
 * - Батчинг: логи пишутся пачками (до 50 шт) раз в 5 секунд
 * - Ленивая очистка: логи старше 30 минут удаляются каждые 5 минут
 * - Одна транзакция на батч вместо отдельной на каждый лог
 * - Упрощенное форматирование JSON (без pretty-print)
 */
import { db, type LogRecord } from '../offline/db'
import { v4 as uuidv4 } from 'uuid'

// Сохраняем оригинальные методы консоли
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
}

// Храним последние 200 логов в памяти (уменьшено с 500)
const memoryLogs: Array<{ level: 'info' | 'warn' | 'error'; message: string; timestamp: string }> = []
const MAX_MEMORY_LOGS = 200

// Батчинг: накапливаем логи и пишем в IndexedDB пачками раз в 5 секунд
const logsBatch: Array<LogRecord> = []
const BATCH_SIZE = 50
const BATCH_INTERVAL = 5000
let batchTimeoutId: number | null = null
let lastCleanupTime = Date.now()

/**
 * Форматирует аргументы console.log в строку (как браузер это делает)
 * ОПТИМИЗАЦИЯ: Ленивое форматирование только для нужных типов
 */
function formatConsoleArgs(...args: any[]): string {
  return args
    .map(arg => {
      if (arg === null) return 'null'
      if (arg === undefined) return 'undefined'
      if (typeof arg === 'string') return arg
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg)
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`
      try {
        // ОПТИМИЗАЦИЯ: Не используем pretty-print (null, 2) → экономим CPU
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(' ')
}

/**
 * Сбрасывает накопленный батч логов в IndexedDB
 */
async function flushLogsBatch(): Promise<void> {
  if (logsBatch.length === 0) return
  
  const logsToWrite = logsBatch.splice(0, logsBatch.length)
  
  try {
    await db.logs.bulkAdd(logsToWrite)
    
    // Очищаем старые логи каждые 5 минут (> 30 минут)
    const now = Date.now()
    if (now - lastCleanupTime > 300000) { // 5 минут
      lastCleanupTime = now
      const cutoff = new Date(now - 30 * 60 * 1000).toISOString()
      const old = await db.logs.where('timestamp').below(cutoff).toArray()
      if (old.length > 0) {
        await db.logs.bulkDelete(old.map(l => l.id))
      }
    }
  } catch (error) {
    // Не логируем ошибку сохранения в консоль чтобы избежать бесконечного цикла
  }
}

/**
 * Добавляет лог в батч для последующей записи
 * ОПТИМИЗАЦИЯ: Батчинг вместо немедленной записи
 */
function addLogToBatch(level: 'info' | 'warn' | 'error', message: string): void {
  const timestamp = new Date().toISOString()
  
  // Сохраняем в память для мгновенного доступа (это быстро)
  memoryLogs.push({ level, message, timestamp })
  if (memoryLogs.length > MAX_MEMORY_LOGS) {
    memoryLogs.shift() // Удаляем старый лог
  }
  
  // Добавляем в батч для записи в IndexedDB
  const id = uuidv4()
  const record: LogRecord = {
    id,
    level,
    message,
    timestamp,
  }
  logsBatch.push(record)
  
  // Если батч заполнен - сбрасываем немедленно
  if (logsBatch.length >= BATCH_SIZE) {
    flushLogsBatch().catch(() => {})
    if (batchTimeoutId !== null) {
      clearTimeout(batchTimeoutId)
      batchTimeoutId = null
    }
  } else {
    // Иначе планируем сброс через 5 секунд
    if (batchTimeoutId === null) {
      batchTimeoutId = window.setTimeout(() => {
        flushLogsBatch().catch(() => {})
        batchTimeoutId = null
      }, BATCH_INTERVAL)
    }
  }
}

/**
 * Начинает перехват console методов
 */
export function startConsoleCapture(): () => void {
  console.log = (...args: any[]) => {
    const message = formatConsoleArgs(...args)
    originalConsole.log(...args) // Выводим в оригинальную консоль
    addLogToBatch('info', message) // Добавляем в батч (синхронно, быстро)
  }

  console.info = (...args: any[]) => {
    const message = formatConsoleArgs(...args)
    originalConsole.info(...args)
    addLogToBatch('info', message)
  }

  console.warn = (...args: any[]) => {
    const message = formatConsoleArgs(...args)
    originalConsole.warn(...args)
    addLogToBatch('warn', message)
  }

  console.error = (...args: any[]) => {
    const message = formatConsoleArgs(...args)
    originalConsole.error(...args)
    addLogToBatch('error', message)
    // КРИТИЧНО: Ошибки сбрасываем в IndexedDB НЕМЕДЛЕННО (не ждём 5s batch).
    // Если страница перезагрузится через <5s после ошибки, лог НЕ потеряется.
    flushLogsBatch().catch(() => {})
  }

  console.log('[ConsoleCapture] ✅ Console capture started (batched mode)')

  // Возвращаем функцию для остановки перехвата
  return () => {
    // Сбрасываем оставшиеся логи перед остановкой
    flushLogsBatch().catch(() => {})
    if (batchTimeoutId !== null) {
      clearTimeout(batchTimeoutId)
      batchTimeoutId = null
    }
    
    console.log = originalConsole.log
    console.warn = originalConsole.warn
    console.error = originalConsole.error
    console.info = originalConsole.info
    originalConsole.log('[ConsoleCapture] 🛑 Console capture stopped')
  }
}

/**
 * Получает оригинальные методы консоли (для внутреннего использования)
 */
export function getOriginalConsole() {
  return originalConsole
}

/**
 * Очищает все in-memory буферы логов и сбрасывает отложенный timeout.
 * Вызывается при команде clear_cache, чтобы остановить накопление логов
 * и предотвратить запись устаревшего батча после очистки IDB.
 */
export function clearLogsBuffer(): void {
  memoryLogs.length = 0
  logsBatch.length = 0
  if (batchTimeoutId !== null) {
    clearTimeout(batchTimeoutId)
    batchTimeoutId = null
  }
  lastCleanupTime = Date.now() // сброс таймера очистки
}

/**
 * Получает логи из памяти (мгновенно, без обращения к IndexedDB)
 */
export function getMemoryLogs(options?: {
  level?: 'info' | 'warn' | 'error'
  limit?: number
}): Array<{ level: 'info' | 'warn' | 'error'; message: string; timestamp: string }> {
  let filtered = [...memoryLogs]
  
  // Фильтруем по уровню если указан
  if (options?.level) {
    filtered = filtered.filter(log => log.level === options.level)
  }
  
  // Берем последние N логов
  const limit = options?.limit || 100
  return filtered.slice(-limit).reverse() // Последние N логов, новые сверху
}
